import { AbstractClimateService } from '../base/AbstractClimateService';
import type { 
  ClimateApiConfig, 
  ClimateApiRequestOptions 
} from '../base/types';
import type {
  PaginationParams,
  DateRangeParams,
  RegionParams,
  DataSourceParams
} from '../../../types/climate';

/**
 * Coverage Service
 * 
 * Handles all coverage-related API operations by calling our backend
 * proxy endpoints which securely communicate with the external Climate API.
 */
export class CoverageService extends AbstractClimateService {
  
  constructor(config: ClimateApiConfig) {
    super(config);
  }

  /**
   * Get the service-specific path - now points to our backend
   */
  protected getServicePath(): string {
    return '/api/climate';
  }

  /**
   * Get coverage data with optional filtering
   */
  async getCoverage(params?: CoverageListParams /* , _options?: ClimateApiRequestOptions */): Promise<CoverageListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      // Pagination
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());
      
      // Date range
      if (params.start_date) searchParams.append('start_date', params.start_date);
      if (params.end_date) searchParams.append('end_date', params.end_date);
      
      // Region filtering
      if (params.region_id) searchParams.append('region_id', params.region_id);
      if (params.region_ids) searchParams.append('region_ids', params.region_ids);
      
      // Data source filtering
      if (params.data_source) searchParams.append('data_source', params.data_source);
      if (params.variable) searchParams.append('variable', params.variable);
      
      // Coverage-specific filters
      if (params.coverage_type) searchParams.append('coverage_type', params.coverage_type);
      if (params.status) searchParams.append('status', params.status);
      if (params.search) searchParams.append('search', params.search);
    }

    const queryString = searchParams.toString();
    const endpoint = `/coverage${queryString ? `?${queryString}` : ''}`;
    
    return this.get<CoverageListResponse>(endpoint);
  }

  /**
   * Get coverage summary data
   */
  async getCoverageSummary(params?: CoverageSummaryParams /* , _options?: ClimateApiRequestOptions */): Promise<CoverageSummaryResponse> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      if (params.region_id) searchParams.append('region_id', params.region_id);
      if (params.data_source) searchParams.append('data_source', params.data_source);
      if (params.include_metrics !== undefined) searchParams.append('include_metrics', params.include_metrics.toString());
      if (params.group_by) searchParams.append('group_by', params.group_by);
    }

    const queryString = searchParams.toString();
    const endpoint = `/coverage/summary${queryString ? `?${queryString}` : ''}`;
    
    // Our backend returns the data in the expected format, no transformation needed
    return this.get<CoverageSummaryResponse>(endpoint);
  }

  /**
   * Update coverage summary data
   */
  async updateCoverageSummary(data: CoverageSummaryUpdateRequest, options?: ClimateApiRequestOptions): Promise<CoverageSummaryUpdateResponse> {
    return this.post<CoverageSummaryUpdateResponse>('/coverage/summary/update', data, options);
  }

  /**
   * Get coverage details by ID
   */
  async getCoverageById(id: string /* , _options?: ClimateApiRequestOptions */): Promise<CoverageDetailsResponse> {
    return this.get<CoverageDetailsResponse>(`/coverage/${id}`);
  }

  /**
   * Get coverage statistics
   */
  async getCoverageStats(params?: CoverageStatsParams, options?: ClimateApiRequestOptions): Promise<CoverageStatsResponse> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      if (params.region_id) searchParams.append('region_id', params.region_id);
      if (params.time_period) searchParams.append('time_period', params.time_period);
      if (params.group_by) searchParams.append('group_by', params.group_by);
    }

    const queryString = searchParams.toString();
    const endpoint = `/coverage/stats${queryString ? `?${queryString}` : ''}`;
    
    return this.get<CoverageStatsResponse>(endpoint, options);
  }

  /**
   * Check Climate API health through our backend
   */
  async getClimateHealth(): Promise<{ success: boolean; climate_api: unknown }> {
    return this.get<{ success: boolean; climate_api: unknown }>('/health');
  }
}

// Type definitions for coverage service

export interface CoverageListParams extends PaginationParams, DateRangeParams, RegionParams, DataSourceParams {
  coverage_type?: string;
  status?: 'active' | 'inactive' | 'pending' | 'payment_collection_expired';
  search?: string;
}

export interface CoverageSummaryParams {
  region_id?: string;
  data_source?: string;
  include_metrics?: boolean;
  group_by?: 'region' | 'data_source' | 'coverage_type' | 'status';
}

export interface CoverageStatsParams {
  region_id?: string;
  time_period?: 'day' | 'week' | 'month' | 'year';
  group_by?: 'region' | 'data_source' | 'coverage_type';
}

// Response types
export interface CoverageItem {
  id: string;
  name: string;
  description?: string;
  coverage_type: string;
  region_id: string;
  region_name?: string;
  data_source: string;
  variable: string;
  status: 'active' | 'inactive' | 'pending' | 'payment_collection_expired';
  start_date: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, unknown>;
}

export interface CoverageListResponse {
  success: boolean;
  data: {
    coverage: CoverageItem[];
    total: number;
    limit: number;
    offset: number;
  };
  error?: {
    message: string;
    details?: unknown;
  };
}

export interface CoverageSummary {
  total_coverage: number;
  active_coverage: number;
  inactive_coverage: number;
  pending_coverage: number;
  by_region: Array<{
    region_id: string;
    region_name: string;
    count: number;
  }>;
  by_data_source: Array<{
    data_source: string;
    count: number;
  }>;
  by_coverage_type: Array<{
    coverage_type: string;
    count: number;
  }>;
}

export interface CoverageSummaryResponse {
  success: boolean;
  data: CoverageSummary;
  error?: {
    message: string;
    details?: unknown;
  };
}

export interface CoverageSummaryUpdateRequest {
  region_id?: string;
  data_source?: string;
  force_refresh?: boolean;
}

export interface CoverageSummaryUpdateResponse {
  success: boolean;
  data: {
    message: string;
    updated_at: string;
    summary: CoverageSummary;
  };
  error?: {
    message: string;
    details?: unknown;
  };
}

export interface CoverageDetails extends CoverageItem {
  spatial_extent?: {
    bounds: [number, number, number, number]; // [minX, minY, maxX, maxY]
    geometry?: unknown; // GeoJSON geometry
  };
  temporal_extent?: {
    start: string;
    end?: string;
    resolution?: string;
  };
  data_quality?: {
    completeness: number;
    accuracy?: number;
    last_validated?: string;
  };
  statistics?: {
    min_value?: number;
    max_value?: number;
    mean_value?: number;
    std_deviation?: number;
  };
}

export interface CoverageDetailsResponse {
  success: boolean;
  data: CoverageDetails;
  error?: {
    message: string;
    details?: unknown;
  };
}

export interface CoverageStats {
  overview: {
    total_regions_covered: number;
    total_data_points: number;
    average_coverage_per_region: number;
    last_updated: string;
  };
  by_region?: Array<{
    region_id: string;
    region_name: string;
    coverage_count: number;
    data_points: number;
    last_updated: string;
  }>;
  by_data_source?: Array<{
    data_source: string;
    coverage_count: number;
    regions_covered: number;
    last_updated: string;
  }>;
  by_coverage_type?: Array<{
    coverage_type: string;
    count: number;
    average_quality: number;
  }>;
}

export interface CoverageStatsResponse {
  success: boolean;
  data: CoverageStats;
  error?: {
    message: string;
    details?: unknown;
  };
} 